import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { Company } from '../types';
import { companiesAPI } from '../services/api';
import { useUser } from './UserContext';
import { useAuth } from './AuthContext';
import { 
  SESSION_KEYS, 
  saveToSessionStorage, 
  loadFromSessionStorage, 
  clearSessionStorage,
  saveSelectedCompany,
  saveCompanies,
  loadSelectedCompany,
  loadCompanies
} from '../utils/sessionStorage';

interface CompanyContextType {
  selectedCompany: Company | null;
  companies: Company[];
  setSelectedCompany: (company: Company | null) => void;
  loading: boolean;
  error: string | null;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { isAdmin } = useUser();
  const { isAuthenticated } = useAuth();
  const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
  const [companies, setCompaniesState] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdminRef = useRef(isAdmin);

  // Enhanced setSelectedCompany with session storage
  const setSelectedCompany = (company: Company | null) => {
    setSelectedCompanyState(company);
    saveSelectedCompany(company);
  };

  // Enhanced setCompanies with session storage
  const setCompanies = (companiesData: Company[]) => {
    setCompaniesState(companiesData);
    saveCompanies(companiesData);
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesAPI.getAll();
      setCompanies(data);
      console.log('Fetched companies:', data);
      
      // For non-admin users, auto-select company if only one available
      if (!isAdminRef.current && data.length === 1) {
        console.log('Auto-selecting single company:', data[0]);
        setSelectedCompany(data[0]);
      }
    } catch (err) {
      setError('Failed to fetch companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshCompanies = async () => {
    await fetchCompanies();
  };

  // Initialize state from session storage
  const initializeFromSessionStorage = () => {
    const savedCompanies = loadCompanies<Company>();
    const savedSelectedCompany = loadSelectedCompany<Company>();

    if (savedCompanies) {
      setCompaniesState(savedCompanies);
      console.log('Restored companies from session storage:', savedCompanies);
    }

    if (savedSelectedCompany) {
      setSelectedCompanyState(savedSelectedCompany);
      console.log('Restored selected company from session storage:', savedSelectedCompany);
    }
  };

  // Update ref when isAdmin changes
  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);

  // Initialize from session storage when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initializeFromSessionStorage();
      // Only fetch if we don't have companies in session storage
      const savedCompanies = loadCompanies<Company>();
      if (!savedCompanies || savedCompanies.length === 0) {
        fetchCompanies();
      }
    } else {
      // Reset state and clear session storage when not authenticated
      setCompaniesState([]);
      setSelectedCompanyState(null);
      setLoading(false);
      setError(null);
      clearSessionStorage([SESSION_KEYS.COMPANIES, SESSION_KEYS.SELECTED_COMPANY]);
    }
  }, [isAuthenticated]); // Only depend on authentication status

  const value: CompanyContextType = {
    selectedCompany,
    companies,
    setSelectedCompany,
    loading,
    error,
    refreshCompanies,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
